"use client"

import React, { useState, useEffect, useRef } from "react"
import { Send } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { ScrollArea } from "./ui/scroll-area"
import { useSession } from "../context/SessionContext"
import { useTranslation } from "react-i18next"
import { Avatar, AvatarFallback } from "./ui/avatar"

interface Message {
  id: string
  senderId: string
  senderName: string
  content: string
  timestamp: Date
}

interface AssignmentChatDialogProps {
  isOpen: boolean
  onClose: () => void
  assignedBy: string
  assignerName: string
  transferId: number
}

const AssignmentChatDialog: React.FC<AssignmentChatDialogProps> = ({
  isOpen,
  onClose,
  assignedBy,
  assignerName,
  transferId,
}) => {
  const { t } = useTranslation()
  const { session, userInfo, allAssistants } = useSession()
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [typingUserName, setTypingUserName] = useState("")
  const [isConnected, setIsConnected] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (!isOpen || !session?.user?.id) {
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close()
        }
        wsRef.current = null
      }
      setIsConnected(false)
      return
    }

    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      const roomId = `assignment_${transferId}_${[assignedBy, session.user.id].sort().join('_')}`
      
      const serverIp = process.env.REACT_APP_LIVE_CHAT_SERVER
      const wsUrl = `wss://${serverIp}/ws/chat/${roomId}`
      
      const ws = new WebSocket(wsUrl)
      
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        
        ws.send(JSON.stringify({
          type: 'join_room',
          userId: session.user.id,
          assignedBy: assignedBy,
          transferId: transferId
        }))
      }

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data)
        
        if (data.type === 'message') {
          // Buscar el nombre del remitente en allAssistants
          let senderName = data.senderName
          if (!senderName || senderName === 'Usuario') {
            const senderAgent = allAssistants.find((assistant: any) => assistant.id === data.senderId)
            senderName = senderAgent?.name || userInfo?.name || t("unknown_user")
          }
          
          const message: Message = {
            id: data.id || Date.now().toString(),
            senderId: data.senderId,
            senderName: senderName,
            content: data.content,
            timestamp: new Date(data.timestamp)
          }
          setMessages(prev => [...prev, message])
        }
        
        if (data.type === 'typing') {
          if (data.senderId !== session.user.id) {
            setIsTyping(data.isTyping)
            if (data.isTyping) {
              // Buscar el nombre del usuario que está escribiendo
              let typingName = data.senderName
              if (!typingName || typingName === 'Usuario') {
                const typingAgent = allAssistants.find((assistant: any) => assistant.id === data.senderId)
                typingName = typingAgent?.name || t("unknown_user")
              }
              setTypingUserName(typingName)
            } else {
              setTypingUserName("")
            }
          }
        }
      }

      ws.onerror = (error) => {
        console.error('❌ [Chat] Chat WebSocket error:', error)
        setIsConnected(false)
      }

      ws.onclose = (event) => {
        console.log('🔴 [Chat] Chat WebSocket disconnected')
        setIsConnected(false)
      }
    }

    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close()
      }
    }
  }, [isOpen, assignedBy, transferId, session?.user?.id])

  const sendMessage = () => {
    if (!newMessage.trim() || !wsRef.current || !session?.user?.id || !isConnected) return

    if (wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected')
      return
    }

    const message = {
      type: 'message',
      senderId: session.user.id,
      senderName: userInfo?.name || 'Usuario',
      content: newMessage.trim(),
      timestamp: new Date().toISOString(),
      recipientId: assignedBy === session.user.id ? 
        extractRecipientFromRoom() : 
        assignedBy,
      transferId: transferId
    }

    try {
      wsRef.current.send(JSON.stringify(message))
      setNewMessage("")
    } catch (error) {
      console.error('Error sending message:', error)
    }
  }

  const extractRecipientFromRoom = () => {
    const roomId = `assignment_${transferId}_${[assignedBy, session?.user.id].sort().join('_')}`
    const parts = roomId.split('_')
    if (parts.length >= 4) {
      const userId1 = parts[2]
      const userId2 = parts[3]
      return session?.user.id === userId1 ? userId2 : userId1
    }
    return assignedBy
  }

  const getChatPartnerName = () => {
    if (session?.user?.id === assignedBy) {
      const otherUserId = extractRecipientFromRoom()
      const partner = allAssistants.find((assistant: any) => assistant.id === otherUserId)
      return partner?.name || t("unknown_user")
    } else {
      return assignerName
    }
  }

  const handleTyping = (typing: boolean) => {
    if (!wsRef.current || !session?.user?.id || !isConnected) return
    if (wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    try {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        senderId: session.user.id,
        senderName: userInfo?.name || 'Usuario',
        isTyping: typing
      }))
    } catch (error) {
      console.error('Error sending typing indicator:', error)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
      handleTyping(false)
    }
  }

  const handleInputWithTimeout = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value)
    
    const hasContent = e.target.value.length > 0
    
    if (hasContent) {
      handleTyping(true)
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        handleTyping(false)
      }, 1000)
    } else {
      handleTyping(false)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }

  const handleClose = () => {
    setMessages([])
    setNewMessage("")
    setIsTyping(false)
    setTypingUserName("")
    setIsConnected(false)
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close()
    }
    wsRef.current = null

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
    
    onClose()
  }

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="w-8 h-8 bg-primary text-white text-xs">
                  {getChatPartnerName()
                    .split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span>{t("chat_with")} {getChatPartnerName()}</span>
                <span className="text-xs text-muted-foreground">
                  {isConnected ? t("connected") : t("connecting")}
                </span>
              </div>
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex flex-col h-96">
          <ScrollArea className="flex-1 p-4 border rounded-md">
            <div className="space-y-4">
              {!isConnected && (
                <div className="text-center text-muted-foreground text-sm py-4">
                  <div className="animate-pulse">{t("connecting_to_chat")}</div>
                </div>
              )}
              
              {messages.length === 0 && isConnected ? (
                <div className="text-center text-muted-foreground text-sm py-8">
                  {t("start_conversation")}
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.senderId === session?.user?.id
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.senderId === session?.user?.id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.senderId === session?.user?.id
                            ? "text-primary-foreground/70"
                            : "text-muted-foreground"
                        }`}
                      >
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              
              {isTyping && typingUserName && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {typingUserName} {t("is_typing")}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div ref={messagesEndRef} />
          </ScrollArea>

          <div className="flex gap-2 mt-4">
            <Input
              value={newMessage}
              onChange={handleInputWithTimeout}
              onKeyPress={handleKeyPress}
              placeholder={isConnected ? t("type_message") : t("connecting")}
              className="flex-1 border rounded-md"
              disabled={!isConnected}
            />
            <Button onClick={sendMessage} disabled={!newMessage.trim() || !isConnected}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignmentChatDialog